import { Injectable } from '@angular/core';
import { AvisoService } from './aviso.service';

/**
 * Qual projeto está aberto (ADR-0072).
 *
 * **A chave é `celebracao`, e não `celebracaoId`.** Seis telas liam
 * `sessionStorage.getItem('celebracaoId')` — uma chave que *nada* no front grava. O que o
 * sistema grava desde sempre é o objeto inteiro da celebração em JSON, escrito por 9 telas de
 * execução e lido em 156 pontos como `JSON.parse(...).id`.
 *
 * Este serviço existe para que esse `JSON.parse` aconteça **num lugar só** (ADR-0072 §3.2):
 * `FluxoCaixaComponent` está montado em 10 módulos de rota e `PainelAnalisePcComponent` em 11,
 * um por tipo de projeto. Corrigir rota a rota são onze lugares para esquecer um.
 */
@Injectable()
export class ContextoDeProjetoService {

  /**
   * As chaves onde o projeto aberto pode estar, na ordem em que valem.
   *
   * É a **mesma lista** que `core/utils/numero-sigrp.ts:33` já usava para achar o número SIGRP
   * do projeto — não uma lista nova. Se as duas divergirem, o número do cabeçalho e o recorte
   * da consulta passam a falar de projetos diferentes.
   */
  private static readonly CHAVES = [
    'celebracao',
    'modExecucaoCelebracao',
    'pthpVisualizarPlano',
    'repasse',
  ];

  constructor(private aviso: AvisoService) { }

  /** A celebração aberta, ou `null`. */
  celebracao(): any {
    for (const chave of ContextoDeProjetoService.CHAVES) {
      const valor = this.ler(chave);
      const celebracao = this.extrair(valor);
      if (celebracao && celebracao.id) {
        return celebracao;
      }
    }
    return null;
  }

  /**
   * O id da celebração aberta, ou `null`.
   *
   * Devolve **string** porque é assim que as telas o usam: como parâmetro de consulta.
   */
  celebracaoId(): string {
    const celebracao = this.celebracao();
    return celebracao ? String(celebracao.id) : null;
  }

  temProjetoAberto(): boolean {
    return this.celebracaoId() !== null;
  }

  /**
   * Avisa que não há projeto aberto — **uma vez só** enquanto o aviso ainda está na tela.
   *
   * Sem isto o Centro de custo mostrava a mesma frase duas vezes empilhadas: `ngOnInit` chama
   * `carregar()`, e o clique seguinte em *Pesquisar* (ou na aba) chama de novo. Dois avisos
   * idênticos não informam mais que um.
   *
   * **A regra não mora mais aqui** (ADR-0078 §3.2). Ela estava neste serviço, que é sobre qual
   * projeto está aberto — classe errada para ser reusada pelo tratador de erro, que tinha o
   * mesmo problema. Agora é do `AvisoService`, e aqui só se delega.
   */
  avisarProjetoAusente(mensagem: string): void {
    this.aviso.aviso(mensagem);
  }

  // =================================================================== apoio

  private ler(chave: string): any {
    try {
      const valor = sessionStorage.getItem(chave);
      return valor ? JSON.parse(valor) : null;
    } catch (erro) {
      // sessionStorage bloqueado ou JSON corrompido: é ausência de contexto, não erro de tela.
      return null;
    }
  }

  /**
   * De cada chave sai um formato diferente: `celebracao` é a própria celebração,
   * `pthpVisualizarPlano` e `repasse` a carregam aninhada. Mesmos caminhos de `numero-sigrp.ts`.
   */
  private extrair(fonte: any): any {
    if (!fonte) {
      return null;
    }
    return (fonte.id && fonte)
      || fonte.celebracao
      || (fonte.planoTrabalho && fonte.planoTrabalho.celebracao)
      || (fonte.planoTrabalhoHasProponente
        && fonte.planoTrabalhoHasProponente.planoTrabalho
        && fonte.planoTrabalhoHasProponente.planoTrabalho.celebracao)
      || (fonte.pthp && fonte.pthp.planoTrabalho && fonte.pthp.planoTrabalho.celebracao)
      || null;
  }
}
